import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { DateRangePreset as DateRangePresetType, DateRange } from '@/types';

interface DateRangePresetProps {
  value: DateRangePresetType;
  onChange: (range: DateRange) => void;
}

// Helper to format date as yyyymmdd
function dateToYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

// Calculate date range based on preset
function calculateDateRange(preset: DateRangePresetType): DateRange {
  const end = new Date();
  const start = new Date();

  switch (preset) {
    case 'week':
      start.setDate(end.getDate() - 7);
      break;
    case 'month':
      start.setDate(end.getDate() - 30);
      break;
    case '3months':
      start.setDate(end.getDate() - 90);
      break;
    case 'all':
      return {
        startDate: null,
        endDate: null,
        preset: 'all',
      };
  }

  return {
    startDate: dateToYmd(start),
    endDate: dateToYmd(end),
    preset,
  };
}

const PRESET_LABELS: Record<DateRangePresetType, string> = {
  week: '最近一周',
  month: '最近一月',
  '3months': '最近三月',
  all: '全部',
};

export default function DateRangePreset({ value, onChange }: DateRangePresetProps) {
  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: DateRangePresetType | null) => {
    if (newValue !== null) {
      onChange(calculateDateRange(newValue));
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        时间范围
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            px: 1.5,
            py: 0.5,
            fontSize: '0.8125rem',
            textTransform: 'none',
          },
        }}
      >
        {(Object.keys(PRESET_LABELS) as DateRangePresetType[]).map((preset) => (
          <ToggleButton key={preset} value={preset}>
            {PRESET_LABELS[preset]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

// Export helper for default date range
export { calculateDateRange };
